/**
 * UploadDropzone.tsx — Drag-and-drop file upload area.
 */
import { useRef, useCallback } from 'react';
import { Upload } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ACCEPT } from '@/services/patientFileTypes';

const MAX_FILE_SIZE_MB = 100;

interface UploadDropzoneProps {
  onFilesSelected: (files: File[]) => void;
  uploading: boolean;
  progress: number;
}

export function UploadDropzone({ onFilesSelected, uploading, progress }: UploadDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const processFiles = useCallback((files: File[]) => {
    if (uploading) return;
    const oversized = files.filter((f) => f.size > MAX_FILE_SIZE_MB * 1024 * 1024);
    if (oversized.length > 0) {
      alert(`Files over ${MAX_FILE_SIZE_MB}MB are not allowed: ${oversized.map((f) => f.name).join(', ')}`);
      return;
    }
    const valid = files.filter((f) => f.size <= MAX_FILE_SIZE_MB * 1024 * 1024);
    if (valid.length > 0) onFilesSelected(valid);
  }, [onFilesSelected, uploading]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    processFiles(Array.from(e.dataTransfer.files));
  }, [processFiles]);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    processFiles(Array.from(e.target.files ?? []));
    e.target.value = '';
  }, [processFiles]);

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); }}
      onDrop={handleDrop}
      className="rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 p-6 text-center"
    >
      <Upload className="h-8 w-8 text-slate-400 mx-auto mb-2" />
      {uploading ? (
        <div className="space-y-2">
          <p className="text-sm text-slate-600">Uploading... {progress}%</p>
          <div className="w-full bg-slate-200 rounded-full h-2 max-w-xs mx-auto">
            <div className="bg-blue-500 rounded-full h-2 transition-all" style={{ width: `${progress}%` }} />
          </div>
        </div>
      ) : (
        <>
          <p className="text-sm text-slate-600 mb-2">Drag & drop files here, or</p>
          <Button size="sm" variant="secondary" onClick={() => inputRef.current?.click()}>
            Browse Files
          </Button>
          <p className="text-xs text-slate-400 mt-1">Max {MAX_FILE_SIZE_MB}MB per file</p>
          <input ref={inputRef} type="file" multiple accept={ACCEPT} className="hidden" onChange={handleFileInput} />
        </>
      )}
    </div>
  );
}
